// New versions (Pierre, 2026-10-27: a window left open since the morning still showed
// English skill names hours after the fix shipped). The app is a single page that never
// reloads by itself, so a deploy only reached a child who closed it. The app asks the server
// for the current page when it is shown or focused, and once a minute while it stays on
// screen (an installed app kept in front never fires `visibilitychange`, which is how a parent
// missed a fix for an hour the same evening); if the entry script differs from the one
// running, it says so briefly and reloads. The live channel and its offline queue survive a
// reload (ADR-0022, rule 9); a half-typed message or a voice session would not, so it waits
// for the next check.

const ENTRY = /\/assets\/index-[\w-]+\.js/;
/** At most one check a minute, however often the app is shown. */
const MIN_INTERVAL_MS = 60_000;
/** How long the "new version" line stays before the reload. */
const NOTICE_MS = 1_500;

export interface UpdateDeps {
  /** The entry script the running page loaded, or null when it can't be told (dev). */
  readonly current: string | null;
  /** The server's current page, bypassing every cache. */
  readonly fetchShell: () => Promise<string>;
  /** True while a reload would lose something: unsent text, a voice session. */
  readonly typing: () => boolean;
  readonly reload: () => void;
  readonly now: () => number;
}

/** The entry script named in a page, e.g. `/assets/index-3CSkSe3x.js`. */
export function entryScript(html: string): string | null {
  return ENTRY.exec(html)?.[0] ?? null;
}

/** One check, run when the app becomes visible. Resolves true when it reloaded. */
export function updateChecker(deps: UpdateDeps): () => Promise<boolean> {
  let last = Number.NEGATIVE_INFINITY;
  return async () => {
    if (deps.current === null || deps.now() - last < MIN_INTERVAL_MS) return false;
    last = deps.now();
    let latest: string | null;
    try {
      latest = entryScript(await deps.fetchShell());
    } catch {
      return false; // Offline or a server hiccup: the running version keeps working.
    }
    if (latest === null || latest === deps.current || deps.typing()) return false;
    deps.reload();
    return true;
  };
}

/** Wires the checker to the page: production builds only (see main.tsx). */
export function watchForUpdates(): void {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  const check = updateChecker({
    current: script === null ? null : entryScript(new URL(script.src).pathname),
    fetchShell: async () => {
      const response = await fetch("/", { cache: "no-store", credentials: "same-origin" });
      if (!response.ok) throw new Error(`shell ${String(response.status)}`);
      return response.text();
    },
    typing: () =>
      document.querySelector('.lesson[data-mode="voice"]') !== null ||
      Array.from(
        document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
          "textarea, input[type='text'], input:not([type])",
        ),
      ).some((field) => field.value.trim() !== ""),
    reload: () => {
      showNotice();
      setTimeout(() => {
        window.location.reload();
      }, NOTICE_MS);
    },
    now: () => Date.now(),
  });
  const onScreen = () => {
    if (document.visibilityState === "visible") void check();
  };
  document.addEventListener("visibilitychange", onScreen);
  window.addEventListener("focus", onScreen);
  window.addEventListener("online", onScreen);
  window.addEventListener("pageshow", onScreen);
  setInterval(onScreen, MIN_INTERVAL_MS);
}

/** A short line so the reload is not a surprise; plain DOM, outside React. */
function showNotice(): void {
  const notice = document.createElement("div");
  notice.className = "update-notice";
  notice.setAttribute("role", "status");
  notice.textContent = document.documentElement.lang.startsWith("fr")
    ? "Nouvelle version, on recharge…"
    : "New version, reloading…";
  document.body.append(notice);
}
